// 확장자를 적는 이유: 이 파일은 단위 테스트가 앱 번들 밖(node)에서 그대로 읽는다 — node ESM 은 상대 경로의
// 확장자를 추론하지 않는다.
import { toNumberOrNull } from './numberValue.js'

/**
 * **사업장 「주문 미수락 자동 취소」 설정 — 경로·액션·번역·문구 한 곳.** 의존성 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지).
 *
 * 계약 정본: `ordern-workspace/docs/planning/qr-order-accept-timeout-impl-20260921.md` §3-1(설정) — 이 파일과 다르면 그 문서가
 * 이긴다. 요약:
 *
 * - 경로 `GET|PUT /cust/{custCd}/accept-timeout`. 범위 밖이거나 없는 사업장은 **404**(403 아님 — 존재를 알리지 않는다).
 * - 값은 칸 둘이고 **따로** 물려받는다: 분(`minutes` — `null` = 상위에서 물려받음, `0` = 끔)과 대상(`targets` — `null` =
 *   상위에서 물려받음, `[]` = 대상 없음). 물려받는 순서는 직속 사업장 → 상위 사업장 → … → 루트 → 전체 기본값(`sys_option`).
 * - GET 은 그 사업장 자기 값 + 전체 기본값(`global*`) + 해석 결과(`effective*`) + 토글 전체 목록(`allTargets`) + 칸별 출처
 *   (`*Source` = `SELF | ANCESTOR | GLOBAL`)를 준다. **화면은 상속 규칙을 다시 계산하지 않는다** — 규칙은 서버 순수 함수가
 *   소유한다. 사업장 계정은 옵션 API 를 부를 수 없으므로(부르면 403 = 로그아웃) 전체 기본값도 이 응답에서만 안다.
 * - PUT 은 키가 없으면 안 바꾸고 `null` 이면 상속으로 되돌린다. 화면은 **두 키를 언제나 함께** 보낸다(부분 저장을 만들지 않는다).
 *   응답은 GET 과 같은 모양(저장 뒤 상태)이라 다시 읽지 않는다.
 *
 * 권한은 **세션 액션**이 답한다([ORDER_ACCEPT_POLICY_ACTION]) — 역할 이름을 적지 않는다. 권한 행이 없는 세션에서 이 경로의
 * 403 은 보안 계층이 내고 서버 세션까지 끊으므로(`plugins/http/policy` `READ_DENIED`), 액션이 없으면 **부르지 않는다**.
 */

/** 설정 한 경로 — 읽기(`GET`)·저장(`PUT`)이 같다 */
export function orderAcceptPolicyUrl(custCd) {
  return '/cust/' + custCd + '/accept-timeout'
}

/**
 * 세션 액션 id — appserver 권한 시드(WP2 SQL)의 `sys_action` 과 **같은 문자열**이어야 한다.
 *
 * 이름은 같은 회차의 선례(`StorePausePut`·`StorePauseDelete` — `{엔티티}{기능}{메서드}`)를 따랐다. 시드가 다른 이름을 쓰면
 * 여기 한 곳만 고친다 — 화면·시험은 이 상수만 읽는다.
 */
export const ORDER_ACCEPT_POLICY_ACTION = Object.freeze({
  /** `GET /cust/{custCd}/accept-timeout` */
  READ: 'CustAcceptTimeoutGet',
  /** `PUT /cust/{custCd}/accept-timeout` */
  SAVE: 'CustAcceptTimeoutPut',
})

/**
 * 읽기 spec — `plugins/http/permittedRead` 의 입력(게이트 + `logoutOn403:false` 를 그 함수가 함께 갖는다).
 * 전역 잠금 로딩은 켜지 않는다 — 패널이 자기 자리에서 로딩을 보인다(화면 본체가 아닌 곁가지 조회다).
 */
export function orderAcceptPolicyRead(custCd) {
  return Object.freeze({
    action: ORDER_ACCEPT_POLICY_ACTION.READ,
    url: orderAcceptPolicyUrl(custCd),
    showLoading: false,
  })
}

/**
 * 저장 요청의 axios 옵트아웃 — 임시중단 카드(`STORE_PAUSE_REQUEST_CONFIG`)와 같은 모양이다.
 *
 * - `showErrorMessage:false` — 실패는 **패널 안에서** 말한다(필드 오류는 칸에, 그 밖은 패널 아래 한 줄). 토스트와 두 번 말하지 않는다.
 * - `showLoading:false` — 진행은 누른 버튼이 보인다.
 *
 * **403 로그아웃은 옵트아웃하지 않는다**: 저장 버튼은 저장 액션을 가진 세션에만 살고, 범위 밖 사업장은 서버가 404 로 답한다 —
 * 그래도 403 이 오면 보안 계층이 세션을 끊은 것이라 화면이 되살릴 수 없다(대체 동작이 없다).
 */
export const ORDER_ACCEPT_POLICY_SAVE_CONFIG = Object.freeze({ showErrorMessage: false, showLoading: false })

/**
 * 분 칸의 범위 — 서버 계약(WP2 보고서 §2: `minutes` 0~1440 **정수**, 서버 `MAX_MINUTES`)과 같은 값이다. 칸이 이 범위와 정수로
 * 값을 정리하고, 정리된 값이 곧 폼 값이 된다(패널이 `input` 을 듣는다) — 화면에 보인 값과 저장 본문이 갈리지 않는다(리뷰 X-a).
 */
export const ORDER_ACCEPT_POLICY_MINUTES = Object.freeze({ MIN: 0, MAX: 1440 })

/** 칸별 값의 출처 — 서버 `*Source` 어휘 그대로 */
export const ORDER_ACCEPT_POLICY_SOURCE = Object.freeze({ SELF: 'SELF', ANCESTOR: 'ANCESTOR', GLOBAL: 'GLOBAL' })

/**
 * 토큰 → 화면 이름. **목록이 아니라 라벨 표다** — 렌더 순서와 토큰의 원천은 응답의 `allTargets` 다(화면이 토큰 배열을
 * 하드코딩하지 않는다). 토큰 = `"<SALES_TP val>:<PAY_TP val>"`, 대소문자까지 서버 그대로(WP2 브리프 §4-2).
 */
export const TARGET_LABELS = Object.freeze({
  'Store:PAY_FIRST': '매장 선불',
  'Store:PAY_AFTER': '매장 후불',
  'Delivery:PAY_FIRST': '배달 선불',
  'Delivery:PAY_AFTER': '배달 후불',
  'Packing:PAY_FIRST': '포장 선불',
  'Packing:PAY_AFTER': '포장 후불',
})

/**
 * 토큰 옆에 붙는 한 줄 — 서버의 7값 결제 구분 중 배달 후불 카드·현금 둘이 토글 하나로 접힌다는 사실을 사람에게 말한다.
 * 둘을 따로 켤 수는 없다(계약이 6칸이다). 화면이 할 변환은 없다 — 이 문장뿐이다.
 */
export const TARGET_HINTS = Object.freeze({
  'Delivery:PAY_AFTER': '카드·현금 결제를 모두 포함합니다',
})

/** 매핑에 없는 토큰은 **토큰 문자열 그대로** — 서버가 축을 늘려도 화면이 조용히 항목을 잃지 않는다 */
export function targetLabel(token) {
  return TARGET_LABELS[token] || token
}

/** 토큰 옆 도움말 — 없으면 빈 문자열 */
export function targetHint(token) {
  return TARGET_HINTS[token] || ''
}

/** 화면 문구 — 동적인 문장(출처·현재 적용·전체 기본값)은 아래 함수들이 만든다 */
export const ORDER_ACCEPT_POLICY_TEXT = Object.freeze({
  title: '주문 미수락 자동 취소',
  intro:
    '매장이 주문을 정한 시간 안에 수락하지 않으면 주문을 자동으로 취소하고 결제를 환불합니다. ' +
    '오더엔 주문웹(QR·콜팝)으로 들어온 주문만 대상이며 예약 주문은 제외합니다. ' +
    '이 사업장 아래 모든 매장에 적용되고, 하위 사업장이 따로 정한 값이 있으면 그 값이 우선합니다.',
  minutesLabel: '수락 마감',
  minutesUnit: '분',
  targetsLabel: '자동 취소 대상',
  /** 분 칸의 placeholder 이자 대상의 「물려받기」 체크 — 비움(`null`)의 이름 */
  inherit: '상위 설정 따름',
  save: '자동 취소 설정 저장',
  saved: '자동 취소 설정을 저장했습니다.',
  /** 서버 문장이 없는 실패의 폴백 — 있으면 서버 문장을 그대로 쓴다(`httpErrorMessage`) */
  saveFailed: '자동 취소 설정을 저장하지 못했습니다.',
  readFailed: '자동 취소 설정을 읽지 못했습니다.',
  readDenied: '이 사업장의 자동 취소 설정을 볼 권한이 없습니다.',
})

/** 폼 오류 칸 — 서버 필드 오류(`minutes`·`targets`)가 앉는 자리 */
export function orderAcceptPolicyErrorKeys() {
  return { minutes: '', targets: '' }
}

/** 대상 칸의 번역 — 배열이면 사본, 아니면 `null`(상속). `[]` 는 `[]` 그대로다 — `null` 과 서로로 변질되지 않는다 */
function targetsOrNull(value) {
  return Array.isArray(value) ? value.slice() : null
}

/**
 * 응답 → 편집 폼(새 객체). 경계에서 한 번만 번역한다.
 *
 * - `minutes`: 숫자면 그대로(`0` = 끔), 없거나 읽을 수 없으면 `null`(상속) — `toNumberOrNull` 한 관문을 지난다.
 * - `targets`: 배열이면 사본(`[]` = 대상 없음), 아니면 `null`(상속).
 *
 * @param {?object} response `GET`·`PUT` 응답 본문
 * @returns {{minutes: ?number, targets: ?Array<string>}}
 */
export function toPolicyForm(response) {
  const body = response && typeof response === 'object' ? response : {}

  return { minutes: toNumberOrNull(body.minutes), targets: targetsOrNull(body.targets) }
}

/**
 * 편집 폼 → 저장 본문. **두 키를 언제나 함께** 싣는다 — 키가 없으면 서버가 「안 바꿈」으로 읽어 부분 저장이 된다.
 * `null` 은 「상위에서 물려받음으로 되돌림」이다.
 *
 * @param {{minutes: ?number, targets: ?Array<string>}} form
 * @returns {{minutes: ?number, targets: ?Array<string>}}
 */
export function toPolicyPayload(form) {
  const value = form || {}

  return { minutes: toNumberOrNull(value.minutes), targets: targetsOrNull(value.targets) }
}

/**
 * 분 → `el-input-number` 의 값. 그 컴포넌트에는 **`undefined` 로** 준다 — `null` 은 컴포넌트 안에서 `Number(null) = 0` 이
 * 되어 「비움(상속)」이 조용히 `0`(끔)이 된다. 우리 축의 「없음」은 `null` 이고 번역은 이 한 쌍이 한다(선례 `PlaceBaseAddressField`).
 */
export function minutesInputValue(minutes) {
  return minutes === null || minutes === undefined ? undefined : minutes
}

/** `el-input-number` 의 값 → 분. 비운 칸(`undefined`)은 `null`(상속), `0` 은 `0`(끔) */
export function minutesFromInput(value) {
  return toNumberOrNull(value)
}

/**
 * 출처 문장 — 칸마다 「이 값이 어디서 왔나」.
 *
 * `ANCESTOR` 의 사업장 이름은 **호출자 가시 범위 밖의 조상이면 `null`** 이다(서버가 가린다) — 그때는 이름 없이 말한다.
 * 모르는 출처(서버 어휘가 늘었거나 칸이 없다)는 빈 문자열 — 짐작해 말하지 않는다.
 */
export function policySourceText(source, sourceCustNm) {
  if (source === ORDER_ACCEPT_POLICY_SOURCE.SELF) return '이 사업장 값'
  if (source === ORDER_ACCEPT_POLICY_SOURCE.ANCESTOR) {
    return sourceCustNm ? `상위 사업장 「${sourceCustNm}」 값을 따름` : '상위 사업장 값을 따름'
  }
  if (source === ORDER_ACCEPT_POLICY_SOURCE.GLOBAL) return '전체 기본값을 따름'
  return ''
}

/** 분 값의 말 — `0`·`null`(해석 결과가 꺼짐)은 「자동 취소 안 함」 */
function minutesText(minutes) {
  const value = toNumberOrNull(minutes)

  return value !== null && value > 0 ? `${value}분` : '자동 취소 안 함'
}

/** 대상 목록의 말 — 빈 목록은 「대상 없음」 */
function targetsText(targets) {
  const list = Array.isArray(targets) ? targets : []

  return list.length ? list.map(targetLabel).join('·') : '대상 없음'
}

/** 「현재 적용: …(출처)」 — 출처를 모르면 괄호를 뺀다 */
function currentText(valueText, source, sourceCustNm) {
  const from = policySourceText(source, sourceCustNm)

  return from ? `현재 적용: ${valueText} (${from})` : `현재 적용: ${valueText}`
}

/**
 * 응답 → 화면이 그리는 **저장된 상태**의 말. 편집 중인 폼이 아니라 서버가 마지막으로 준 값이다(저장하면 응답으로 바뀐다).
 *
 * @param {?object} response `GET`·`PUT` 응답 본문
 * @returns {{allTargets: Array<string>, minutesCurrent: string, minutesHint: string, targetsCurrent: string, targetsHint: string}}
 */
export function orderAcceptPolicyView(response) {
  const body = response && typeof response === 'object' ? response : {}
  const hasGlobalMinutes = body.globalMinutes !== null && body.globalMinutes !== undefined
  const globalMinutes = hasGlobalMinutes ? `(현재 ${minutesText(body.globalMinutes)})` : ''
  const globalTargets = Array.isArray(body.globalTargets) ? `(현재: ${targetsText(body.globalTargets)})` : ''

  return Object.freeze({
    allTargets: Array.isArray(body.allTargets) ? body.allTargets.slice() : [],
    minutesCurrent: currentText(minutesText(body.effectiveMinutes), body.minutesSource, body.minutesSourceCustNm),
    minutesHint: `비우면 상위 사업장 값을, 상위에 값이 없으면 전체 기본값${globalMinutes}을 따릅니다. 0이면 자동 취소하지 않습니다.`,
    targetsCurrent: currentText(targetsText(body.effectiveTargets), body.targetsSource, body.targetsSourceCustNm),
    targetsHint: `「상위 설정 따름」이면 상위 사업장 값을, 상위에 값이 없으면 전체 기본값${globalTargets}을 따릅니다. 모두 끄면 자동 취소 대상이 없습니다.`,
  })
}
